/* UserInputCommandPalette.tsx generated by @compiled/babel-plugin v0.36.1 */
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./UserInputCommandPalette.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
// Entry file
/* eslint-disable @atlaskit/editor/no-re-export */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bindAll } from 'bind-event-listener';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl-next';
import { AssistiveText } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex } from '@atlaskit/primitives/compiled';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { checkAssistanceServiceFreeGenerateFg } from '@atlassian/editor-ai-common/utils/check-assistance-service-fg';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { UsePageKnowledgeToggleFooter } from '../../components/Footer/UsePageKnowledgeToggleFooter';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import { RefinementTag } from '../../components/RefinementTag/RefinementTag';
import { Suggestions } from '../../components/Suggestions/Suggestions';
import { useAgentRefinementTagProps } from '../../hooks/use-agent-refinement-tag-props';
import { AnnouncementBanners } from './AnnouncementBanners/AnnouncementBanners';
import { getNextSuggestion } from './getNextSuggestion';
import messages from './messages';
import { reduceSuggestionsIntoNestedSuggestions } from './reduceSuggestionsIntoNestedSuggestions';
var columnLayout = null;

/**
 * Scroll margin is used to ensure that the modal won't be
 * too close to the top of the editor, which may sometimes
 * cover the modal slightly.
 */
var scrollMarginStyles = null;
var caretHiddenStyle = null;
var PromptHeaderWrapperStyles = {
  default: "_ca0qutpp _u5f3utpp _19bvutpp _n3tdutpp _1tkeidpf _1o9zidpf"
};
var PromptHeaderWrapperTeam25Styles = {
  default: "_18zrpxbi _1rjcutpp _1tkeidpf _1o9zidpf"
};
var userInputFloatingContainerStyle = null;
/**
 * This screen is used when the user is interacting with the AI command palette.
 *
 * ---
 *
 * If there is no preset prompt label, show the suggestions.
 * Otherwise, show the preset prompt label and no suggestions.
 */
export var UserInputCommandPalette = function UserInputCommandPalette(_ref) {
  var appearance = _ref.appearance,
    inputValue = _ref.inputValue,
    inputADFValue = _ref.inputADFValue,
    onInputUpdate = _ref.onInputUpdate,
    onADFUpdate = _ref.onADFUpdate,
    presetPromptLabel = _ref.presetPromptLabel,
    onSubmit = _ref.onSubmit,
    onEscapeKeyPressed = _ref.onEscapeKeyPressed,
    onRefinementTagRemove = _ref.onRefinementTagRemove,
    onBack = _ref.onBack,
    placeholder = _ref.placeholder,
    getSuggestions = _ref.getSuggestions,
    suggestionsHeading = _ref.suggestionsHeading,
    focusInputRef = _ref.focusInputRef,
    PromptEditor = _ref.PromptEditor,
    agent = _ref.agent,
    hideLegacySubmitCancelButtons = _ref.hideLegacySubmitCancelButtons,
    announcementBanners = _ref.announcementBanners,
    pageKnowledgeProps = _ref.pageKnowledgeProps;
  var promptWithSuggestionsRef = useRef(null);
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;

  // There is a situation when using the arrow keys to navigate through the suggestions
  // where Prosemirror will move the caret to the end of the prompt input's placeholder
  // as the placeholder is done via a content-editable bit of text. This solution allows
  // the caret to not jump around while pressing 'down' while not having to edit the core
  // functionality of Prosemirror.
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isCaretHidden = _useState2[0],
    setIsCaretHidden = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isShowingMoreSuggestions = _useState4[0],
    setIsShowingMoreSuggestions = _useState4[1];

  /**
   * Scroll prompt into view as suggestions are not always fully visible
   * when PromptInput is autofocused.
   *
   * Note: We use useLayoutEffect here vs useEffect as it is synchronous and operates
   * after the DOM has been updated with all mutations etc. This is important as
   * Safari has a bug where it can not correctly calculate scroll positions and glitches
   * out causing all content to disappear without it.
   *
   * Performance implications should be minimal as we are not affecting the rendering
   * of content, but rather using it for calculations and triggering scroll events.
   *
   * https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
   *
   * ᕕ( ᐛ )ᕗ
   */
  useLayoutEffect(function () {
    // Add event listener to handle escape key press
    var element = promptWithSuggestionsRef.current;

    // If the element is not mounted, do nothing
    if (!element) {
      return;
    }
    if (!fg('platform_editor_ai_scroll_into_view_fix')) {
      element.scrollIntoView({
        block: 'nearest',
        behavior: 'auto'
      });
    }

    /**
     * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
     * other escape key handlers will not override this behaviour.
     * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
     */
    var handleEscapeKeyDown = function handleEscapeKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onEscapeKeyPressed();
      }
    };

    /**
     * Handle KeyUp event to prevent unexpected escape behaviours when
     * there are other escape handlers outside of component (e.g. in product)
     */
    var handleEscapeKeyUp = function handleEscapeKeyUp(event) {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    return bindAll(element, [{
      type: 'keydown',
      listener: handleEscapeKeyDown
    }, {
      type: 'keyup',
      listener: handleEscapeKeyUp
    }]);
  }, [promptWithSuggestionsRef, onEscapeKeyPressed]);
  var suggestions = useMemo(function () {
    var _getSuggestions;
    /**
     * If there is an agent, show the suggestions for that agent.
     * The suggestions in this scenario are the agents conversation starters.
     * And we also want to hide the group heading in this case.
     */
    if (agent) {
      return getSuggestions(inputValue, agent);
    }

    /*
     * If there is no refinement tag, show the suggestions.
     * Otherwise, show the refinement tag and no suggestions.
     */
    var nestedSuggestions = (_getSuggestions = getSuggestions(inputValue)) !== null && _getSuggestions !== void 0 ? _getSuggestions : [];

    // If no input provided, provide the grouped results, excluding children
    if (!inputValue.length) {
      nestedSuggestions = nestedSuggestions.reduce(reduceSuggestionsIntoNestedSuggestions, []);

      // If a prompt label exists, as well as children, show the children
      // otherwise use the default behaviour of showing all matching options.
      if (presetPromptLabel) {
        var _nestedSuggestions$fi, _nestedSuggestions$fi2;
        nestedSuggestions = (_nestedSuggestions$fi = (_nestedSuggestions$fi2 = nestedSuggestions.find(function (s) {
          var _s$nestingConfig;
          return ((_s$nestingConfig = s.nestingConfig) === null || _s$nestingConfig === void 0 ? void 0 : _s$nestingConfig.parentTitle) === presetPromptLabel;
        })) === null || _nestedSuggestions$fi2 === void 0 ? void 0 : _nestedSuggestions$fi2.childSuggestions) !== null && _nestedSuggestions$fi !== void 0 ? _nestedSuggestions$fi : [];
      }
    }
    var showMoreSuggestionIndex = nestedSuggestions.findIndex(function (suggestion) {
      return suggestion.value === 'show-more';
    });
    if (showMoreSuggestionIndex !== -1) {
      var originalOnSelect = nestedSuggestions[showMoreSuggestionIndex].onSelect;
      nestedSuggestions[showMoreSuggestionIndex].onSelect = function () {
        originalOnSelect();
        setIsShowingMoreSuggestions(true);
      };
    }
    return nestedSuggestions;
  }, [presetPromptLabel, getSuggestions, inputValue, agent]);
  var hideNestedTag = useMemo(function () {
    return presetPromptLabel && !!suggestions.length;
  }, [suggestions, presetPromptLabel]);
  var agentRefinementTagProps = useAgentRefinementTagProps(agent);
  var refinementTagRef = useRef(null);
  var tag = useMemo(function () {
    return presetPromptLabel === undefined ? undefined : /*#__PURE__*/React.createElement(RefinementTag
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
    , _extends({}, agentRefinementTagProps, {
      label: presetPromptLabel,
      ref: refinementTagRef,
      onClick: onRefinementTagRemove
    }));
  }, [agentRefinementTagProps, presetPromptLabel, onRefinementTagRemove, refinementTagRef]);
  var suggestionValues = useMemo(function () {
    return (suggestions || []).map(function (suggestion) {
      return suggestion.value;
    });
  }, [suggestions]);
  var _useState5 = useState(suggestionValues.length > 0 ? suggestionValues[0] : ''),
    _useState6 = _slicedToArray(_useState5, 2),
    currentSuggestion = _useState6[0],
    _setCurrentSuggestion = _useState6[1];

  /**
   * Handles the behaviour for when a user changes the suggestion.
   *
   * At the moment this is used to highlight the entire editor document for
   * relevant suggestions.
   */
  var handleSuggestionChanged = useCallback(function (suggestion) {
    var suggestionObject = suggestions.find(function (s) {
      return s.value === suggestion;
    });
    suggestionObject === null || suggestionObject === void 0 || suggestionObject.onSuggestionFocused();
  }, [suggestions]);
  var setCurrentSuggestion = useCallback(function (newValue) {
    handleSuggestionChanged(newValue);
    _setCurrentSuggestion(newValue);
  }, [_setCurrentSuggestion, handleSuggestionChanged]);

  // Select first option by default
  useEffect(function () {
    if (suggestionValues.includes(currentSuggestion) || suggestionValues.length === 0) {
      return;
    }
    setCurrentSuggestion(suggestionValues[0]);
  }, [currentSuggestion, suggestionValues, setCurrentSuggestion]);

  // Calculate visible suggestions list
  var visibleSuggestionValues = useMemo(function () {
    var allSuggestions = suggestionValues.filter(function (suggestion) {
      return suggestion !== 'show-more';
    });
    if (isShowingMoreSuggestions || inputValue.length > 0) {
      return allSuggestions;
    }
    var agentSuggestions = suggestionValues.filter(function (suggestion) {
      return suggestion.startsWith('agent:');
    });
    var minNumOfGeneralSuggestions = 4;
    var maximumResultCount = 9;
    if (agentSuggestions.length === 0 || allSuggestions.length <= maximumResultCount ||
    // If we only have the min. number of suggestions,
    // we return the full list because we don't need to show the show more button
    allSuggestions.length - agentSuggestions.length <= minNumOfGeneralSuggestions) {
      return allSuggestions;
    }

    // The maximum number of suggestions is 8 when agents are present
    // We take the extra 1 so that we can put the show more button in place
    // of the last expected suggestion.
    var remainingSuggestions = maximumResultCount - agentSuggestions.length - 1;

    // If the user favourites enough agents, the resulting count will be negative and
    // force the entire list to show. We want to encourage it to limit the slicing to 1
    // item in this case.
    if (remainingSuggestions < minNumOfGeneralSuggestions) {
      remainingSuggestions = minNumOfGeneralSuggestions;
    }
    return [].concat(_toConsumableArray(suggestionValues.slice(0, remainingSuggestions)), ['show-more'], _toConsumableArray(agentSuggestions));
  }, [inputValue.length, isShowingMoreSuggestions, suggestionValues]);
  var visibleSuggestions = useMemo(function () {
    var output = suggestions.filter(function (suggestion) {
      return visibleSuggestionValues.includes(suggestion.value);
    });
    return output;
  }, [suggestions, visibleSuggestionValues]);
  var onArrowKeyDown = useCallback(function (event) {
    // Ignore if any key modifiers are held
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }

    // If theres one or fewer suggestions, no need to navigate and allow normal
    // multiline text traversal
    if (visibleSuggestionValues.length <= 1) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        {
          setIsCaretHidden(true);
          var nextSuggestion = getNextSuggestion(visibleSuggestionValues, currentSuggestion, event.key);
          setCurrentSuggestion(nextSuggestion);
          event.preventDefault();
          event.stopPropagation();
          break;
        }
      default:
        {
          return;
        }
    }
  }, [currentSuggestion, setCurrentSuggestion, visibleSuggestionValues]);
  var debouncedSetIsCaretHidden = useMemo(function () {
    return debounce(function () {
      setIsCaretHidden(false);
    }, 300);
  }, []);
  var onArrowKeyUp = useCallback(function () {
    debouncedSetIsCaretHidden();
  }, [debouncedSetIsCaretHidden]);
  var setSuggestion = useCallback(function (suggestion) {
    setCurrentSuggestion(suggestion.value);
  }, [setCurrentSuggestion]);
  var suggestionDiv = /*#__PURE__*/React.createElement(Suggestions, {
    appearance: appearance,
    suggestions: visibleSuggestions,
    suggestionsHeading: suggestionsHeading,
    value: currentSuggestion,
    setSuggestion: setSuggestion,
    hideGroupHeading: !!agent
  });
  var onFormSubmit = useCallback(function () {
    if (currentSuggestion === '' || (suggestions === null || suggestions === void 0 ? void 0 : suggestions.length) === 0) {
      onSubmit();
      return;
    }
    var suggestion = (suggestions || []).find(function (suggestion) {
      return suggestion.value === currentSuggestion;
    });
    if (suggestion) {
      suggestion.onSelect();
    }
    setCurrentSuggestion('');
  }, [currentSuggestion, suggestions, setCurrentSuggestion, onSubmit]);

  // if a refinement tag like brainstorm was selected, we continue showing these legacy buttons
  var showLegacySubmitCancelButtons = !hideLegacySubmitCancelButtons && Boolean(presetPromptLabel && !agent);
  var enableLinksInPromptEditor = checkAssistanceServiceFreeGenerateFg();
  var promptPlaceholder = useMemo(function () {
    var defaultPlaceholder = placeholder;
    if (agent) {
      return formatMessage(messages.rovoAgentsPalettePlaceholder);
    }
    if (tag) {
      return defaultPlaceholder;
    }
    return defaultPlaceholder;
  }, [agent, formatMessage, placeholder, tag]);
  var promptForm = /*#__PURE__*/React.createElement(Flex, {
    testId: "prompt-header-wrapper",
    xcss: fg('ai-smart-button-team-25') ? PromptHeaderWrapperTeam25Styles.default : PromptHeaderWrapperStyles.default
  }, /*#__PURE__*/React.createElement(PromptForm, {
    type: "user-input",
    showButtons: showLegacySubmitCancelButtons,
    placeholder: promptPlaceholder,
    value: inputValue,
    adfValue: inputADFValue,
    onFormSubmit: onFormSubmit,
    onInputChange: onInputUpdate,
    onADFChange: onADFUpdate,
    tag: hideNestedTag ? undefined : tag,
    focusInputRef: focusInputRef,
    showLogo: false,
    onInputKeyDown: onArrowKeyDown,
    onInputKeyUp: onArrowKeyUp,
    PromptEditor: PromptEditor,
    enableLinks: enableLinksInPromptEditor,
    showBack: !!presetPromptLabel,
    onBack: onBack,
    refinementTagRef: refinementTagRef
  }));
  return /*#__PURE__*/React.createElement("div", {
    ref: promptWithSuggestionsRef,
    "data-testid": "user-input-command-palette-screen"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: ax(["_zulp1b66 _1e0c1txw _2lx21bp4 _4cvr1y6m", "_j4orxy5q", isCaretHidden && "_1n5d1j28", "user-input-command-palette-screen ".concat(AI_MODAL_SCREEN_CLASS)])
  }, /*#__PURE__*/React.createElement(VisuallyHidden, null, formatMessage(messages.commandPaletteAriaName)), /*#__PURE__*/React.createElement(FloatingContainer, {
    disablePadding: true,
    "data-testid": "prompt-form-screen",
    rainbowBorder: false,
    enableMaxHeight: true,
    className: ax(["_1e0c1txw _2lx21bp4 _ca0qze3t _u5f3ze3t _n3tdze3t _19bvze3t"])
  }, promptForm, announcementBanners && /*#__PURE__*/React.createElement(AnnouncementBanners, {
    banners: announcementBanners
  }), fg('editor_a11y_announce_editor_ai_list_update') && /*#__PURE__*/React.createElement(AssistiveText, {
    assistiveText: formatMessage(messages.suggestionsUpdated, {
      numberOfSuggestions: visibleSuggestions.length
    }),
    isInFocus: visibleSuggestions.length > 0,
    id: "suggestions-updated",
    "data-testid": "suggestions-updated"
  }), suggestions || inputValue ? suggestionDiv : null, (pageKnowledgeProps === null || pageKnowledgeProps === void 0 ? void 0 : pageKnowledgeProps.isVisible) && /*#__PURE__*/React.createElement(UsePageKnowledgeToggleFooter, {
    onChange: pageKnowledgeProps.onToggle,
    isChecked: pageKnowledgeProps.isEnabled
  })));
};